import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, concatMap, catchError, withLatestFrom } from 'rxjs/operators';
import { TreelistDataService } from '../services/treelist-data.service';
import {
  TreelistDataRefreshFailedAction,
  TreelistDataRefreshSuccessAction,
  RefreshTreelistDataAction,
} from '../actions/treelist.actions';
import { TreelistsState } from '../reducers/treelist.reducers';
import { translateDataSourceResultGroups } from '@progress/kendo-data-query';
import { Observable, of, Subscriber } from 'rxjs';
import { getTreelistById } from '../selectors/treelist.selectors';
import { TreelistPayload } from '../models/treelistPayload';
import { Treelist } from '../models/treelist.model';
import { TreelistDataResponse } from '../services/treelist-data.service';

export function withBetterLatestFrrom<TSource, TSecond>(second: (input: TSource) => Observable<TSecond>) {
  return function latestFrom(source: Observable<TSource>): Observable<[TSource, TSecond]> {
    return new Observable<[TSource, TSecond]>((subscriber: Subscriber<[TSource, TSecond]>) => {
      const subscription = source
        .pipe(concatMap((action: TSource) => of(action).pipe(withLatestFrom(second(action)))))
        .subscribe({
          next: (value: [TSource, TSecond]) => {
            try {
              subscriber.next(value);
            } catch (err) {
              subscriber.error(err);
            }
          },
          error: (err: unknown) => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
      return subscription;
    });
  };
}

@Injectable()
export class TreelistsEffects {
  private treelistsService = inject(TreelistDataService);
  private actions$ = inject(Actions);
  private store = inject<Store<TreelistsState>>(Store);

  loadTreelistData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RefreshTreelistDataAction),
      withBetterLatestFrrom((x) => this.store.select(getTreelistById(x.id))),
      concatMap(([action, treelist]: [ReturnType<typeof RefreshTreelistDataAction>, Treelist | undefined]) => {
        const settings = action.settings ?? treelist?.state ?? {};
        const addition = action.addition ?? null;
        const payload: TreelistPayload = action.payload ?? treelist?.payload ?? {};
        return this.treelistsService.fetchData(action.urlPostfix, settings, addition, payload).pipe(
          map((x: TreelistDataResponse) => {
            const hasGroups = Boolean(settings.group?.length);
            return TreelistDataRefreshSuccessAction({
              id: action.id,
              treeListResponse: (hasGroups
                ? translateDataSourceResultGroups(x.data)
                : x.data) as unknown[] /*, total: x.total*/,
              treeListErrors: x.errors,
            });
          }),
          catchError(() => {
            return of(TreelistDataRefreshFailedAction({ id: action.id }));
          })
        );
      })
    )
  );
}
